import * as React from 'react';
import { Button, ButtonGroup, Form, Stack, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { PageTitle, Section, useCacheQuery, useQueryOptionsFromSearchParams } from 'gossamer-front-end';
import { EventExtendedView, EventExtendedViewRow } from 'cb-model';
import { displayLocaleTime, Filter, getFilter, QueryOptions } from 'gossamer-unified';
import { convertDateTimeFromUTCToLocal } from 'gossamer-unified';
import { useInstitutionFilter } from '../institution/useInstitutionFilter';

const startOfLocalDayToUtcIso = (localDate: string) => {
  if (!localDate) return '';
  return new Date(`${localDate}T00:00:00`).toISOString();
};

const endOfLocalDayToUtcIso = (localDate: string) => {
  if (!localDate) return '';
  return new Date(`${localDate}T23:59:59`).toISOString();
};

const getDefaultRange = () => {
  const start = new Date();
  const end = new Date();
  end.setDate(end.getDate() + 30);
  return {
    from: convertDateTimeFromUTCToLocal(start.toISOString()).substring(0, 10),
    to: convertDateTimeFromUTCToLocal(end.toISOString()).substring(0, 10),
  };
};

const buildInitialQueryOptions = (): QueryOptions => {
  const range = getDefaultRange();
  return {
    where: {
      and: [
        getFilter('institution_id', 'eq', '', 'hidden'),
        getFilter('start_at', 'gt', startOfLocalDayToUtcIso(range.from)),
        getFilter('start_at', 'lt', endOfLocalDayToUtcIso(range.to)),
      ],
    },
    order: [['start_at', 'ASC']],
    limit: 1000,
  };
};

type Group = {
  dateKey: string;
  label: string;
  rows: EventExtendedViewRow[];
};

const makeGroups = (rows: EventExtendedViewRow[]): Group[] => {
  const groups = new Map<string, EventExtendedViewRow[]>();
  rows.forEach((row) => {
    const localDate = convertDateTimeFromUTCToLocal(row.start_at).substring(0, 10);
    const existing = groups.get(localDate) || [];
    existing.push(row);
    groups.set(localDate, existing);
  });

  return Array.from(groups.entries())
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([dateKey, groupRows]) => {
      const d = new Date(`${dateKey}T00:00:00`);
      const label = d.toLocaleDateString(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
      return { dateKey, label, rows: groupRows };
    });
};

export const EventCalendar = (): React.JSX.Element => {
  const initialQueryOptions = useInstitutionFilter(buildInitialQueryOptions());
  const [queryOptions, setQueryOptions] = useQueryOptionsFromSearchParams(EventExtendedView, initialQueryOptions);

  const andFilters = (queryOptions.where?.and || []) as Filter[];
  const fromFilter = andFilters.find((f) => f.fieldId === 'start_at' && f.operator === 'gt');
  const toFilter = andFilters.find((f) => f.fieldId === 'start_at' && f.operator === 'lt');

  const fromLocal = fromFilter?.value
    ? convertDateTimeFromUTCToLocal(String(fromFilter.value)).substring(0, 10)
    : getDefaultRange().from;
  const toLocal = toFilter?.value
    ? convertDateTimeFromUTCToLocal(String(toFilter.value)).substring(0, 10)
    : getDefaultRange().to;

  const setRange = (from: string, to: string) => {
    const next: QueryOptions = {
      ...queryOptions,
      where: {
        and: [
          ...andFilters.filter((f) => !(f.fieldId === 'start_at' && (f.operator === 'gt' || f.operator === 'lt'))),
          getFilter('start_at', 'gt', startOfLocalDayToUtcIso(from)),
          getFilter('start_at', 'lt', endOfLocalDayToUtcIso(to)),
        ],
      },
      order: [['start_at', 'ASC']],
      limit: 1000,
    };
    setQueryOptions(next);
  };

  const query = useCacheQuery<EventExtendedViewRow>(EventExtendedView, queryOptions);
  const rows = (query.data || []).slice().sort((a: any, b: any) => (a.start_at < b.start_at ? -1 : 1));
  const groups = React.useMemo(() => makeGroups(rows), [rows]);

  const localTime = (iso: string) => displayLocaleTime(iso);

  const quickSet = (days: number) => {
    const start = new Date();
    const end = new Date();
    end.setDate(end.getDate() + days);
    const from = convertDateTimeFromUTCToLocal(start.toISOString()).substring(0, 10);
    const to = convertDateTimeFromUTCToLocal(end.toISOString()).substring(0, 10);
    setRange(from, to);
  };

  return (
    <>
      <PageTitle title="Event Calendar" subtitle="Agenda view of events for your institution" />

      <Section>
        <Stack direction="horizontal" gap={3} style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Form.Group controlId="from">
            <Form.Label>From</Form.Label>
            <Form.Control type="date" value={fromLocal} onChange={(e) => setRange(e.currentTarget.value, toLocal)} />
          </Form.Group>
          <Form.Group controlId="to">
            <Form.Label>To</Form.Label>
            <Form.Control type="date" value={toLocal} onChange={(e) => setRange(fromLocal, e.currentTarget.value)} />
          </Form.Group>
          <div style={{ marginLeft: 'auto' }}>
            <ButtonGroup size="sm">
              <Button variant="outline-secondary" onClick={() => quickSet(7)}>
                Next 7 days
              </Button>
              <Button variant="outline-secondary" onClick={() => quickSet(30)}>
                Next 30 days
              </Button>
              <Button variant="outline-secondary" onClick={() => quickSet(90)}>
                Next 90 days
              </Button>
            </ButtonGroup>
          </div>
        </Stack>
      </Section>

      <Section>
        {query.isFetching && !query.data && <div>Loading events…</div>}
        {!query.isFetching && groups.length === 0 && <div>No events in this date range.</div>}

        {groups.map((g) => (
          <div key={g.dateKey} style={{ marginBottom: 28 }}>
            <div style={{ fontWeight: 700, marginBottom: 10 }}>{g.label}</div>
            <Table hover responsive size="sm" style={{ marginBottom: 0 }}>
              <thead>
                <tr>
                  <th style={{ width: 160 }}>Time</th>
                  <th>Event</th>
                  <th style={{ width: 220 }}>Location</th>
                  <th style={{ width: 120 }}></th>
                </tr>
              </thead>
              <tbody>
                {g.rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      {localTime(row.start_at)}–{localTime(row.end_at)}
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{row.title}</div>
                      <div style={{ color: 'var(--bs-gray-600)', fontSize: 12 }}>{row.type}</div>
                    </td>
                    <td>{row.location_type === 'online' ? 'Online' : row.location}</td>
                    <td style={{ textAlign: 'right' }}>
                      <Link to={`/admin/event/display/${row.id}`}>Open</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ))}
      </Section>
    </>
  );
};
